import React, { useState, useEffect } from 'react';
import { X, Smartphone, Share } from 'lucide-react';

const STORAGE_KEYS = {
  visits: 'kurso_visit_count',
  dismissed: 'kurso_pwa_dismissed_at',
  installed: 'kurso_pwa_installed',
};

const DISMISS_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

function isInStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// Step Guide for iOS
function IOSGuide({ onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/80 z-[200] flex items-end justify-center"
      onClick={onClose}
      data-testid="ios-guide-overlay"
    >
      <div
        className="w-full max-w-md bg-[#1a1927] border border-white/10 rounded-t-3xl p-6 pb-8 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="ios-guide-sheet"
      >
        <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-5" />
        <h3 className="text-white font-bold text-lg mb-4 text-center">
          Installer Kurso sur votre iPhone
        </h3>
        <div className="space-y-4">
          {[
            { step: '1', icon: '⬆️', title: 'Appuyez sur "Partager"', desc: "Appuyez sur le bouton Partager en bas de Safari" },
            { step: '2', icon: '📌', title: 'Sur l\'écran d\'accueil', desc: "Faites défiler et appuyez sur « Sur l'écran d'accueil »" },
            { step: '3', icon: '✅', title: 'Appuyez sur « Ajouter »', desc: "Confirmez en appuyant sur Ajouter en haut à droite" },
            { step: '4', icon: '📱', title: 'C\'est installé !', desc: "Kurso est maintenant sur votre écran d'accueil" },
          ].map(({ step, icon, title, desc }) => (
            <div key={step} className="flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-[#ff6b6b]/15 border border-[#ff6b6b]/30 flex items-center justify-center text-xs font-bold text-[#ff6b6b] shrink-0">
                {step}
              </div>
              <div>
                <p className="text-white text-sm font-medium">{icon} {title}</p>
                <p className="text-[#a0a0ab] text-xs mt-0.5">{desc}</p>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={onClose}
          data-testid="ios-guide-close-btn"
          className="btn-primary w-full mt-6 py-3 text-sm"
        >
          Compris !
        </button>
      </div>
    </div>
  );
}

export default function PWAInstallBanner({ showOnFirstDashboard = false }) {
  const [show, setShow] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Don't show if already installed
    if (isInStandaloneMode()) return;
    if (localStorage.getItem(STORAGE_KEYS.installed)) return;
    if (!isMobile()) return;

    // Track visit count
    const visits = parseInt(localStorage.getItem(STORAGE_KEYS.visits) || '0') + 1;
    localStorage.setItem(STORAGE_KEYS.visits, String(visits));

    // Check dismiss TTL
    const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEYS.dismissed) || '0');
    if (Date.now() - dismissedAt < DISMISS_TTL) return;

    // Require at least 2 visits (or first dashboard visit if enabled)
    const minVisits = showOnFirstDashboard ? 1 : 2;
    if (visits < minVisits) return;

    if (isIOS()) {
      // iOS: always show banner (no beforeinstallprompt on Safari)
      setShow(true);
    } else {
      // Android: wait for beforeinstallprompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShow(true);
      };
      window.addEventListener('beforeinstallprompt', handler);

      // Listen for installation
      window.addEventListener('appinstalled', () => {
        localStorage.setItem(STORAGE_KEYS.installed, '1');
        setShow(false);
      });

      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, [showOnFirstDashboard]);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEYS.dismissed, String(Date.now()));
    setShow(false);
  };

  const handleInstallAndroid = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem(STORAGE_KEYS.installed, '1');
    }
    setDeferredPrompt(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <>
      {/* Bottom slide-up banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[150] px-4 pb-4 pointer-events-none"
        data-testid="pwa-install-banner"
      >
        <div className="pointer-events-auto max-w-md mx-auto bg-[#1a1927] border border-white/15 rounded-2xl p-4 shadow-2xl shadow-black/50 animate-slide-up">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#ff6b6b]/10 border border-[#ff6b6b]/20 flex items-center justify-center shrink-0">
              {isIOS() ? <Share size={20} className="text-[#ff6b6b]" /> : <Smartphone size={20} className="text-[#ff6b6b]" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">
                {isIOS() ? 'Ajoutez Kurso à votre écran d\'accueil' : 'Installez Kurso sur votre téléphone'}
              </p>
              <p className="text-[#a0a0ab] text-xs mt-0.5">
                Accès rapide, fonctionne hors ligne
              </p>
            </div>
            <button onClick={handleDismiss} className="text-[#a0a0ab] hover:text-white p-1" data-testid="pwa-banner-close">
              <X size={16} />
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            {isIOS() ? (
              <button
                onClick={() => setShowIOSGuide(true)}
                className="flex-1 py-2 rounded-xl bg-[#ff6b6b] text-white text-sm font-semibold hover:bg-[#ff5555] transition-colors"
                data-testid="pwa-ios-guide-btn"
              >
                Comment faire ?
              </button>
            ) : (
              <button
                onClick={handleInstallAndroid}
                className="flex-1 py-2 rounded-xl bg-[#ff6b6b] text-white text-sm font-semibold hover:bg-[#ff5555] transition-colors"
                data-testid="pwa-install-android-btn"
              >
                Installer
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="px-4 py-2 rounded-xl text-[#a0a0ab] text-sm hover:text-white transition-colors"
              data-testid="pwa-banner-later-btn"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>

      {/* iOS step-by-step guide */}
      {showIOSGuide && <IOSGuide onClose={() => { setShowIOSGuide(false); handleDismiss(); }} />}
    </>
  );
}
